import { Space, Tag, theme, Button } from "antd";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { CssVars } from "src/components/CssVars";
import { CustomTooltip } from "src/components/CustomTooltip";
import { stripMarkdown } from "../cardUtils";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

const MAX_TAG_LENGTH = 30;
const TAG_HEIGHT = 24; // Height of a single tag row
const TAG_GAP = 8; // Gap between tags

interface TagsProps {
  tags: string[];
  className?: string;
  tagClassName?: string;
  maxRows?: number;
}

const truncateTag = (tag: string) => {
  const cleanTag = stripMarkdown(tag);
  return cleanTag.length > MAX_TAG_LENGTH
    ? `${cleanTag.slice(0, MAX_TAG_LENGTH)}...`
    : cleanTag;
};

export function Tags({
  tags,
  className,
  tagClassName,
  maxRows = 1,
}: TagsProps) {
  const { token } = theme.useToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState<string[]>([]);
  const [remainingCount, setRemainingCount] = useState(0);
  const tagsRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!tags || tags.length === 0) return;

    const estimateTagWidth = (label: string, charWidth = 8, padding = 12) => {
      return Math.min(label.length, MAX_TAG_LENGTH) * charWidth + padding;
    };

    const calculateVisibleTags = () => {
      const container = containerRef.current;
      if (!container) return;
      const containerWidth = container.offsetWidth - 32;
      let totalWidth = 0;
      let visibleCount = 0;
      const gap = TAG_GAP;
      const moreTagWidth = 60;
      const maxWidth = containerWidth * maxRows;

      for (let i = 0; i < tags.length; i++) {
        const tagWidth = estimateTagWidth(stripMarkdown(tags[i])) + gap;
        const reserve = i < tags.length - 1 ? moreTagWidth : 0;
        if (totalWidth + tagWidth + reserve > maxWidth) break;
        totalWidth += tagWidth;
        visibleCount++;
      }
      setVisibleTags(tags.slice(0, visibleCount));
      setRemainingCount(tags.length - visibleCount);
    };

    const resizeObserver = new ResizeObserver(calculateVisibleTags);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    calculateVisibleTags();
    return () => resizeObserver.disconnect();
  }, [tags, maxRows]);

  if (!tags || tags.length === 0) return null;
  const tagClasses = tagClassName || "vzbl-label-sm vzbl-pill-sm vzbl-pill-tag";

  const renderTag = (tag: string) => {
    const cleanTag = stripMarkdown(tag);
    const displayTag = truncateTag(tag);

    return cleanTag.length > MAX_TAG_LENGTH ? (
      <CustomTooltip content={cleanTag} placement="bottom" size="medium">
        <Tag className={tagClasses}>{displayTag}</Tag>
      </CustomTooltip>
    ) : (
      <Tag className={tagClasses}>{displayTag}</Tag>
    );
  };

  const displayTags = isExpanded ? tags : visibleTags;
  const hasMoreTags = remainingCount > 0 && !isExpanded;

  return (
    <div ref={containerRef} className={className} style={{ width: "100%" }}>
      <div
        ref={tagsRef}
        style={{
          width: "100%",
          maxHeight: isExpanded
            ? "none"
            : `${TAG_HEIGHT * maxRows + TAG_GAP}px`,
          overflow: "hidden",
        }}
      >
        <Space size={[TAG_GAP, TAG_GAP]} wrap>
          {(Array.isArray(displayTags) ? displayTags : []).map((tag, index) => (
            <React.Fragment key={index}>{renderTag(tag)}</React.Fragment>
          ))}
          {hasMoreTags && (
            <CustomTooltip
              content={
                <div style={{ maxWidth: 300 }} className="vzbl-text-3">
                  {(Array.isArray(tags) ? tags : [])
                    .slice(visibleTags.length)
                    .map((tag, index) => (
                      <div key={index} style={{ marginBottom: 4 }}>
                        {stripMarkdown(tag)}
                      </div>
                    ))}
                </div>
              }
              placement="bottom"
              size="medium"
            >
              <Tag
                className={tagClasses}
                onClick={() => setIsExpanded(true)}
                style={{ cursor: "pointer" }}
              >
                +{remainingCount} more{" "}
                <DownOutlined style={{ marginLeft: 4 }} />
              </Tag>
            </CustomTooltip>
          )}
          {isExpanded && (
            <Tag
              className={tagClasses}
              onClick={() => setIsExpanded(false)}
              style={{ cursor: "pointer" }}
            >
              Show less <UpOutlined style={{ marginLeft: 4 }} />
            </Tag>
          )}
        </Space>
      </div>
    </div>
  );
}
